// src/SearchBookingPage.jsx
import { useParams } from "react-router-dom";
import { useState } from "react";
import AppHeader from "@/layouts/AppHeader";
import WhatsAppFab from "@/components/WhatsAppFab";
import Sitemap from "@/components/Sitemap";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Award } from "lucide-react";
import { AppFooter } from "@/layouts/AppFooter";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { useRegion } from "@/context/RegionContext";

// Define TypeScript interfaces for the API response
interface ApiResponse {
  success: boolean;
  message: string;
}

const SearchBookingPage: React.FC = () => {
  const { region } = useRegion();
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(
        `${process.env.API_URL}reservations/request-access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        // Attempt to parse error message from response
        const errorData: ApiResponse = await response.json();
        throw new Error(errorData.message || "Failed to request access.");
      }

      const data: ApiResponse = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        throw new Error(data.message || "Something went wrong.");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Europcar {region.countryName}: Manage my bookings</title>
        <meta name="description" content="Manage my bookings. Fill in your details to find your booking." />
        <meta name="keywords" content={region.seoKeywords} data-hid="keywords"/>
    </Helmet>
      <WhatsAppFab />

      <AppHeader
        className="bg-neutral-100 pb-4 px-4 md:px-32 relative w-full z-30"
        textColor="text-black"
        borderColor="border-black"
      />

      <div className="max-w-2xl mx-auto p-6 bg-white rounded-md mt-32 mb-40">
        {/* Back Button */}
        <a
          href="/"
          className="flex items-center text-green-600 hover:text-green-800 mb-6"
        >
          {/* SVG Icon */}
          <svg
            className="w-6 h-6 mr-2"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M3.412 12.968a1.397 1.397 0 01.008-1.99l7.64-7.538c1.33-1.335 3.328.669 1.997 2.005L8.6 9.917c-.367.368-.243.666.28.666h10.707c.78 0 1.412.635 1.412 1.417 0 .783-.632 1.417-1.412 1.417H8.88c-.52 0-.646.299-.28.667l4.457 4.471c1.33 1.337-.666 3.34-1.997 2.004l-7.648-7.591z" />
          </svg>
          <span className="text-lg font-medium">Back</span>
        </a>

        {/* Booking Section */}
        <div>
          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Manage my bookings
          </h2>
          <p className="text-gray-600 mb-6">
            Fill in your details to find your booking
          </p>

          {/* Form */}
          <form onSubmit={handleSearch}>
            {/* Email Input */}
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-gray-700 font-semibold mb-2"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading || success}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 text-gray-600">
                <p>{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-4 text-gray-600">
                <p>Search your booking in email.</p>
              </div>
            )}

            {/* Search Button */}
            <Button
              type="submit"
              className={`w-full py-2 px-4 rounded-md text-black font-bold`}
              disabled={loading || success}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </form>

          {/* Sign In Prompt (Optional) */}
          {/* <p className="text-center text-gray-600 mt-6">
            Already have an account?{' '}
            <a
              href="/EBE/module/driver/AuthenticateDrivers1000.do"
              className="text-blue-600 font-semibold hover:underline"
            >
              Sign in
            </a>{' '}
            to access all your bookings.
          </p> */}
        </div>
      </div>

      <AppFooter />
    </>
  );
};

export default SearchBookingPage;
